import React, { useState } from 'react';

import { isDefined } from '../../../../utils/variables';
import { ElementObj } from '../../../../viewer/elements/element-obj';
import { InputHandler } from '../../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useNonce } from '../../../hooks/use-nonce';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { CategoryContext } from '../state/category-state';

type Options = {
  onAssetChanged?: () => void;
};

export const useAsset = (asset: ElementObj, options?: Options) => {
  const [canBeDisplayed, setCanBeDisplayed] = useState(asset.canBeDisplayed());
  const { expandedAssetId, toggle: toggleAsset } = React.useContext(CategoryContext);
  const [_, refresh] = useNonce();

  const toggleVisibility = React.useCallback(
    async (v?: boolean) => {
      await asset.toggleVisibility(isDefined(v) ? v : !asset.visible);
      refresh();
    },
    [asset, refresh],
  );

  useSubscribe(SubscriptionTopic.AssetChanged, (x) => {
    if (x.uuid === asset?.uuid) {
      options?.onAssetChanged?.();
      setCanBeDisplayed(asset.canBeDisplayed());
    }
  });

  useSubscribe(SubscriptionTopic.SkyviewAssetVisibilityChanged, (message) => {
    if (message?.assetUuid === asset?.uuid) {
      closeInspectorTool();
      options?.onAssetChanged?.();
      setCanBeDisplayed(asset.canBeDisplayed());
    }
  });

  useSubscribe(SubscriptionTopic.ShowOnlySpecificAssets, async (message) => {
    // Show asset if in list, otherwise hide.
    await toggleVisibility(message.assetUuids.includes(asset.uuid));
  });

  const toggleAssetExpanded = (assetId: string) => {
    closeInspectorTool();
    toggleAsset(assetId);
  };

  /**
   * Closes the volume inspector tool (dialog) if opened, by selecting the select tool.
   */
  const closeInspectorTool = () => {
    if (InputHandler.instance.isVolumeInspectorToolActive()) {
      InputHandler.instance.setTool('select');
    }
  };

  return {
    assetSelected: expandedAssetId === asset.uuid,
    toggleAsset: toggleAssetExpanded,
    toggleVisibility,
    canBeDisplayed,
  };
};
